/**
 * Shared constants: plans, limits, locales, roles.
 * These mirror the master spec (sections 2, 6) and are the single source of truth
 * for plan limits across the API, dashboard and landing.
 */

// --- Locales ---------------------------------------------------------------

export const LOCALES = ['en', 'fr', 'nl', 'de', 'es', 'pt'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
  nl: 'Nederlands',
  de: 'Deutsch',
  es: 'Español',
  pt: 'Português',
};

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

// --- Organisation roles ----------------------------------------------------

export const ORG_ROLES = ['owner', 'admin', 'viewer'] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

// --- Plans & limits --------------------------------------------------------

export const PLANS = ['free', 'pro', 'agency'] as const;
export type Plan = (typeof PLANS)[number];

export interface PlanLimits {
  /** Maximum number of projects. `null` means unlimited. */
  maxProjects: number | null;
  /** Monthly visit quota. */
  maxVisitsPerMonth: number;
  /** Data retention in days (13 months ≈ 395 days). */
  retentionDays: number;
  /** Maximum organisation members. `null` means unlimited. */
  maxMembers: number | null;
  /** Number of client-facing shared links. `null` means unlimited. */
  sharedLinks: number | null;
}

export const LIMITS: Record<Plan, PlanLimits> = {
  free: {
    maxProjects: 1,
    maxVisitsPerMonth: 10_000,
    retentionDays: 30,
    maxMembers: 1,
    sharedLinks: 0,
  },
  pro: {
    maxProjects: 5,
    maxVisitsPerMonth: 200_000,
    retentionDays: 395,
    maxMembers: 3,
    sharedLinks: 1,
  },
  agency: {
    maxProjects: null,
    maxVisitsPerMonth: 2_000_000,
    retentionDays: 395,
    maxMembers: null,
    sharedLinks: null,
  },
};

/** Monthly price in EUR (excl. VAT). */
export const PLAN_PRICES_EUR: Record<Plan, number> = {
  free: 0,
  pro: 19,
  agency: 79,
};

/** Grace window (days) after a plan's monthly visit quota is first exceeded,
 * during which visits keep being recorded before new ones are dropped. */
export const USAGE_GRACE_DAYS = 7;

// --- Projects --------------------------------------------------------------

/** Grace window (days) after a site is deleted, during which the original project
 * (same public key, full history) can be restored before it is permanently purged. */
export const PROJECT_GRACE_DAYS = 30;

export const DEFAULT_PROJECT_SETTINGS = {
  anonymizeIp: false,
  alertThresholds: { spike: 20, score: 70 },
  trackerInstalledAt: null,
  connectionVerifiedAt: null,
} as const;

/** Maximum number of additional domains per project. */
export const MAX_ADDITIONAL_DOMAINS = 20;
